import React, { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import ModalComponent from '../../../../../../components/modal/ModalComponent';
import { FieldModel } from '../../../../../../models/form/field';
import { FieldIcon, FieldType } from '../../../../../../models/form/fieldType';
import { Rule } from '../../../../../../models/form/rule';
import { deleteField, setSelectedField, setSelectedFieldById } from '../../../../../../slices/formadmin';
import { RootState } from '../../../../../../store';
import { getSecuritySection, SecurityModel, SecuritySections } from '../../../../../../models/security/security';
import { Tab, Tabs } from 'react-bootstrap';
import FieldProperties from '../../properties/FieldProperties';
import FieldOptions from '../../options/FieldOptions';
import { getText } from '../../../../../../utilities/CultureUtilities';

export interface Props {
  children: React.ReactNode;
  field: FieldModel;
  rules: Rule[];
}

const Options: FC<Props> = ({ children, field, rules }: Props) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>('properties');
  const [fieldInRule, setFieldInRule] = useState(false);
  const [formSecurity, setFormSecurity] = useState<SecurityModel | null>(null);
  const security = useSelector((state: RootState) => state.organization.organization?.security);
  const selectedLanguage = useSelector((state: RootState) => state.formAdmin.selectedLanguage) as string;
  const intl = useIntl();
  const dispatch = useDispatch();

  const optionsFieldList = [
    FieldType.OptionList,
    FieldType.Select,
    FieldType.PaymentOptionList,
    FieldType.PaymentChoiceList,
    FieldType.Products,
  ];

  const backgroundColor = showDeleteModal ? 'rgba(255, 0, 0, 0.2)' : '';

  useEffect(() => {
    verifyFieldIsInRules();
  }, [rules]);

  useEffect(() => {
    if (security) {
      setFormSecurity(getSecuritySection(security, SecuritySections.FORM));
    }
  }, [security]);

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleOpenDeleteModal = () => {
    setShowDeleteModal(true);
  };

  const handleCloseEditModal = () => {
    console.log('UAH');
    setShowEditModal(false);
    setSelectedTabById('properties');
  };

  const handleOpenEditModal = () => {
    setShowEditModal(true);
  };

  const renderOptions = (fieldType: FieldType) => {
    return optionsFieldList.includes(fieldType);
  };

  const setSelectedTabById = (id: string | null) => {
    if (id != null) {
      setSelectedTab(id);
    } else {
      setSelectedTab('properties');
    }
  };

  const confirmDelete = () => {
    dispatch(deleteField(field));
    dispatch(setSelectedField(undefined));
  };

  const verifyFieldIsInRules = () => {
    for (const rule of rules) {
      for (const orConditions of rule.conditions) {
        for (const andCondition of orConditions) {
          if (andCondition.fieldId === field.id) {
            setFieldInRule(true);
            return;
          }
        }
      }
      for (const target of rule.targets) {
        if (target.fieldId === field.id) {
          setFieldInRule(true);
          return;
        }
      }
    }
    setFieldInRule(false);
  };

  let buttonsDeleteModal;
  if (fieldInRule) {
    buttonsDeleteModal = [{ text: 'Cancel', buttonVariant: 'secondary', onClick: handleCloseDeleteModal }];
  } else {
    buttonsDeleteModal = [
      { text: 'Cancel', buttonVariant: 'secondary', onClick: handleCloseDeleteModal },
      { text: 'Delete', buttonVariant: 'danger', onClick: confirmDelete },
    ];
  }

  return (
    <div style={{ backgroundColor: backgroundColor, borderRadius: 4 }}>
      <ModalComponent
        closeSettings={handleCloseDeleteModal}
        buttons={buttonsDeleteModal}
        show={showDeleteModal}
        modalHeading="Attention!"
        modalSize="lg"
      >
        {!fieldInRule && (
          <div className="d-flex justify-content-center flex-column">
            <span>
              <FormattedMessage id="optionsWrapper.delete.confirmation" />
            </span>
            <span className="field-text">{getText(field.text, selectedLanguage)}</span>
          </div>
        )}
        {fieldInRule && (
          <div>
            <FormattedMessage id="optionsWrapper.delete.errorRule" />
          </div>
        )}
      </ModalComponent>
      <ModalComponent
        closeSettings={handleCloseEditModal}
        buttons={[]}
        show={showEditModal}
        modalHeading=""
        modalSize="lg"
        className="mx-4"
      >
        <div className="d-flex flex-column">
          <Tabs
            activeKey={selectedTab}
            onSelect={(k) => {
              setSelectedTabById(k);
            }}
            className="mb-3 field-edit-tabs"
          >
            <Tab
              eventKey="properties"
              key="properties"
              className={'px-5 pb-3'}
              title={intl.formatMessage({ id: 'formAdmin.properties' })}
            >
              <FieldProperties />
              {renderOptions(field.type) && <FieldOptions />}
            </Tab>
          </Tabs>
        </div>
      </ModalComponent>
      {children}
      <span
        id={field.id}
        className={`mdi ${FieldIcon[field.type]} settings-icon`}
        onClick={() => dispatch(setSelectedFieldById(field.id))}
      />
      {formSecurity?.update && <span className="mdi mdi-pencil-outline edit-icon" onClick={handleOpenEditModal} />}
      {formSecurity?.delete && <span className="mdi mdi-close delete-icon" onClick={handleOpenDeleteModal} />}
    </div>
  );
};

export default Options;
